/**
 * Which renderer the graph explorer mounts: a feature gate, with a URL override.
 *
 * `?engine=v2` opts into the bespoke SVG/HTML engine; `?engine=v1` is the way back
 * to React Flow and the only way to clear a stored opt-in. The choice is latched for
 * the tab session and stripped from the address bar, so a demo URL does not have to
 * stay on screen and a reload of the clean URL keeps the selected engine.
 *
 * Modelled on `showcaseMode.ts`, which already solves the same problem, and resolved
 * the same way: an explicit URL request outranks the gate in both directions.
 *
 *   1. `?engine=v2` forces the new renderer on, so a demo does not have to wait for
 *      gate propagation.
 *   2. `?engine=v1` clears a latched opt-in **and** beats a passing gate — the way
 *      back to React Flow once the gate is rolling out.
 *   3. Otherwise the {@link GRAPH_ENGINE_FEATURE_GATE}, defaulting to React Flow.
 */

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags/fg';

/** Registered in this package's `package.json` under `platform-feature-flags`. */
export const GRAPH_ENGINE_FEATURE_GATE = 'twg-microsite-graph-engine-v2';

const PARAM = 'engine';
const SESSION_KEY = 'twg-microsite-graph-engine';

export type GraphEngineChoice = 'react-flow' | 'stage';

/** In-memory latch, so stripping the URL cannot lose the choice mid-session. */
let latchedChoice: GraphEngineChoice | null = null;

type UrlRequest = 'absent' | GraphEngineChoice;

/**
 * Reads the parameter from the search string *and* from behind the hash.
 *
 * The microsite serves deep links as `#your-graph?engine=v2`, so a hash-only
 * parameter has to be honoured or the switch silently does nothing on exactly the
 * links people share.
 */
function readUrlRequest(): UrlRequest {
	const win = getDocument()?.defaultView;
	if (!win) {
		return 'absent';
	}

	const fromSearch = new URLSearchParams(win.location.search).get(PARAM);
	const hash = win.location.hash;
	const hashQueryIndex = hash.indexOf('?');
	const fromHash =
		hashQueryIndex === -1 ? null : new URLSearchParams(hash.slice(hashQueryIndex + 1)).get(PARAM);

	const raw = (fromSearch ?? fromHash)?.toLowerCase();
	if (raw === 'v2' || raw === 'stage' || raw === 'engine') {
		return 'stage';
	}
	if (raw === 'v1' || raw === 'react-flow' || raw === 'reactflow') {
		return 'react-flow';
	}
	return 'absent';
}

function readSessionChoice(): GraphEngineChoice | null {
	const win = getDocument()?.defaultView;
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- session-scoped demo latch; deliberately not persisted beyond the tab.
		const stored = win?.sessionStorage?.getItem(SESSION_KEY);
		return stored === 'stage' || stored === 'react-flow' ? stored : null;
	} catch {
		// Private-mode or blocked storage. The in-memory latch still covers this page
		// view; only survival across a reload is lost.
		return null;
	}
}

function writeSessionChoice(choice: GraphEngineChoice | null): void {
	const win = getDocument()?.defaultView;
	try {
		if (choice === null) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- see above.
			win?.sessionStorage?.removeItem(SESSION_KEY);
			return;
		}
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- see above.
		win?.sessionStorage?.setItem(SESSION_KEY, choice);
	} catch {
		// Ignored for the same reason as above.
	}
}

/** Removes `engine=…` from both the search string and any hash query. */
function stripParamFromUrl(): void {
	const win = getDocument()?.defaultView;
	if (!win) {
		return;
	}

	const url = new URL(win.location.href);
	let changed = url.searchParams.has(PARAM);
	url.searchParams.delete(PARAM);

	const hash = url.hash;
	const hashQueryIndex = hash.indexOf('?');
	if (hashQueryIndex !== -1) {
		const fragment = hash.slice(0, hashQueryIndex);
		const params = new URLSearchParams(hash.slice(hashQueryIndex + 1));
		if (params.has(PARAM)) {
			params.delete(PARAM);
			const rest = params.toString();
			url.hash = rest ? `${fragment}?${rest}` : fragment;
			changed = true;
		}
	}

	if (!changed) {
		return;
	}
	win.history.replaceState(win.history.state, '', url.toString());
}

/**
 * Consumes `?engine=…` from the landing URL: latches it, then strips it.
 *
 * Call once at bootstrap, before the explorer mounts — the host reads the choice on
 * its first render and would otherwise keep the pre-strip answer. Idempotent: a
 * second call finds nothing left in the URL and keeps what was latched.
 */
export function consumeEngineUrlOverride(): GraphEngineChoice | null {
	const request = readUrlRequest();

	if (request === 'absent') {
		latchedChoice = latchedChoice ?? readSessionChoice();
		return latchedChoice;
	}

	latchedChoice = request;
	// `?engine=v1` clears the stored opt-in rather than persisting a negative, so a
	// later `?engine=v2` is not fighting a sticky opt-out. It still latches in memory,
	// which is what decides this page view.
	writeSessionChoice(request === 'stage' ? 'stage' : null);
	stripParamFromUrl();
	return latchedChoice;
}

/**
 * The engine to mount. Defaults to React Flow until the gate says otherwise.
 *
 * An explicit request outranks the gate in **both** directions. That is what makes
 * `?engine=v1` a usable escape hatch during a rollout rather than a no-op, and it is
 * why the latch is consulted before `fg` rather than merged with it.
 *
 * The URL is still read directly as a last resort so hosts that never call
 * {@link consumeEngineUrlOverride} — Storybook, tests, any other embedder — behave
 * the same way.
 */
export function resolveGraphEngine(): GraphEngineChoice {
	const choice = latchedChoice ?? readSessionChoice();
	if (choice !== null) {
		return choice;
	}
	const request = readUrlRequest();
	if (request !== 'absent') {
		return request;
	}
	return fg(GRAPH_ENGINE_FEATURE_GATE) ? 'stage' : 'react-flow';
}

/** True when the bespoke engine should be used. */
export function isStageEngineEnabled(): boolean {
	return resolveGraphEngine() === 'stage';
}

/** Test-only: clears the in-memory latch. Do not call from app code. */
export function __resetEngineModeForTests(): void {
	latchedChoice = null;
}
